"use client";

import { useState, useEffect, createContext, useContext } from 'react';
import { onAuthStateChanged, signOut, Auth, User, Unsubscribe } from 'firebase/auth';
import { getAuthInstance } from '@/firebase';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Spinner } from '@/components/ui/animations';
import { LogOut, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRouter } from 'next/navigation';


// Create a context for auth state to avoid duplicate initialization
const AuthContext = createContext<{
  auth: Auth | null;
  user: User | null;
  loading: boolean;
  error: string | null;
}>({
  auth: null,
  user: null,
  loading: true,
  error: null
});

// Provider component to initialize auth once at a high level
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState({
    auth: null as Auth | null,
    user: null as User | null,
    loading: true,
    error: null as string | null
  });

  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null;
    
    const initialize = async () => {
      try {
        const authInstance = await getAuthInstance();
        
        setState(prev => ({ ...prev, auth: authInstance }));
        
        unsubscribe = onAuthStateChanged(
          authInstance,
          (user) => setState(prev => ({ ...prev, user, loading: false })),
          (error) => setState(prev => ({ 
            ...prev, 
            error: `Auth error: ${error.message}`, 
            loading: false 
          }))
        );
      } catch (error: any) {
        console.error("Auth initialization error:", error);
        setState(prev => ({ 
          ...prev, 
          error: error.message || "Failed to initialize authentication", 
          loading: false 
        }));
      }
    };
    
    initialize();
    
    return () => { if (unsubscribe) unsubscribe(); };
  }, []);

  return (
    <AuthContext.Provider value={state}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth() {
  return useContext(AuthContext);
}

// HeaderNavAuth component for navigation
export function HeaderNavAuth() {
  // Use the shared auth context instead of reinitializing
  const { auth, user, loading } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { toast } = useToast();

  const handleLogout = async () => {
    if (!auth) return;
    
    setIsLoggingOut(true);
    
    try {
      await signOut(auth);
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account",
      });
    } catch (error: any) {
      console.error('Logout error:', error);
      toast({
        title: "Logout failed",
        description: error.message || "Failed to log out. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (loading) {
    return <Skeleton className="h-9 w-20 rounded-md" />;
  }

  if (user) {
    return (
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" asChild>
          <Link href="/dashboard">Dashboard</Link>
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleLogout} 
          disabled={isLoggingOut}
          className="flex items-center gap-1"
        >
          {isLoggingOut ? (
            <>
              <Spinner size="sm" className="mr-1" />
              <span>Logging out</span>
            </>
          ) : (
            <>
              <LogOut className="h-3.5 w-3.5 mr-1" />
              <span>Logout</span>
            </>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" asChild>
        <Link href="/login">Log in</Link>
      </Button>
      <Button variant="default" size="sm" asChild>
        <Link href="/signup">Sign up</Link>
      </Button>
    </div>
  );
}

// Props for the HeroButton component
interface HeroButtonProps {
  className?: string;
  signedInText?: string;
  signedOutText?: string;
  signedInHref?: string;
  signedOutHref?: string;
  size?: 'default' | 'sm' | 'lg';
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  showArrow?: boolean;
}

// Update the HeroButton component to directly use Firebase auth
export function HeroButton({
  className,
  signedInText = "Go to Dashboard",
  signedOutText = "Get Started Free",
  signedInHref = "/dashboard",
  signedOutHref = "/signup",
  size = "default",
  variant = "default",
  showArrow = false
}: HeroButtonProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    // Direct Firebase auth subscription
    let unsubscribe: Unsubscribe | null = null;
    
    const initAuth = async () => {
      try {
        const authInstance = await getAuthInstance();
        
        unsubscribe = onAuthStateChanged(
          authInstance,
          (currentUser) => {
            setUser(currentUser);
            setLoading(false);
          },
          (error) => {
            console.error("Auth state error:", error);
            setLoading(false);
          }
        );
      } catch (error) {
        console.error("Failed to initialize auth:", error);
        setLoading(false);
      }
    };
    
    initAuth();
     
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const targetPath = user ? signedInHref : signedOutHref;
    router.push(targetPath);
  };

  if (loading) {
    return (
      <Skeleton 
        className={cn(
          "h-11 w-40 rounded-md", 
          className
        )} 
      />
    );
  }

  return (
    <Button 
      size={size} 
      variant={variant}
      onClick={handleClick}
      className={cn(
        "font-medium transition-all",
        showArrow && "group",
        className
      )}
    >
      <span>{user ? signedInText : signedOutText}</span>
      {showArrow && (
        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
      )}
    </Button>
  );
}
// Additional components for the homepage
export function ClientSideCounter() {
  const [count, setCount] = useState(0);
  
  return (
    <div className="flex flex-col items-center gap-2">
      <p className="text-center">Count: {count}</p>
      <Button onClick={() => setCount(count + 1)}>Increment</Button>
    </div>
  );
}

// Feature toggle component with client-side interactivity
export function FeatureToggle({ 
  title, 
  description, 
  defaultEnabled = false 
}: { 
  title: string; 
  description: string; 
  defaultEnabled?: boolean;
}) {
  const [isEnabled, setIsEnabled] = useState(defaultEnabled);
  
  return (
    <div className="flex items-center justify-between p-4 border rounded-lg">
      <div>
        <h3 className="font-medium">{title}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      <button
        className={cn(
          "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
          isEnabled ? "bg-blue-600" : "bg-gray-200 dark:bg-gray-700"
        )}
        onClick={() => setIsEnabled(!isEnabled)}
      >
        <span
          className={cn(
            "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
            isEnabled ? "translate-x-5" : "translate-x-1"
          )}
        />
      </button>
    </div>
  );
}

// Usage example:
// <AuthProvider>
//   <HeaderNavAuth />
//   <HeroButton className="mt-6" />
// </AuthProvider>